"use client";
export const dynamic = "force-dynamic";

import Link from "next/link";
import { motion } from "framer-motion";
import {
  Instagram,
  Layers,
  Users2,
  Bot,
  Zap,
  Smartphone,
  ArrowUpLeft,
  ArrowLeft,
  Compass,
} from "lucide-react";
import SiteHeader from "@/components/landing/SiteHeader";
import SiteFooter from "@/components/landing/SiteFooter";
import {
  SolutionShell,
  Orbs,
  GridBg,
  Section,
  SectionHeader,
  GiantStats,
  MiniTestimonials,
  FAQ,
  FinalCTA,
  HeroBadge,
  HeroTitle,
} from "@/components/landing/solutions/SolutionSections";

const solutions = [
  {
    to: "/solutions/instagram",
    icon: Instagram,
    title: "مدیریت اینستاگرام",
    desc: "پاسخ خودکار، آنالیز، قرعه‌کشی و صف ارسال — مخصوص اینستاگرام.",
    chip: "Instagram‑First",
  },
  {
    to: "/solutions/omnichannel",
    icon: Layers,
    title: "صندوق چندکاناله",
    desc: "اینستا، تلگرام، واتس‌اپ و وب در یک inbox مشترک.",
    chip: "Omnichannel",
  },
  {
    to: "/solutions/crm",
    icon: Users2,
    title: "CRM فارسی",
    desc: "مخاطبین، Pipeline، گزارش درآمد و کمپین.",
    chip: "Sales OS",
  },
  {
    to: "/solutions/ai-assistant",
    icon: Bot,
    title: "دستیار هوش مصنوعی",
    desc: "ترندی روی دانش برند شما آموزش می‌بیند.",
    chip: "AI Native",
  },
  {
    to: "/solutions/auto-reply",
    icon: Zap,
    title: "پاسخ خودکار",
    desc: "Flow Builder بصری، A/B و گزارش Funnel.",
    chip: "No-code Flow",
  },
  {
    to: "/solutions/sms",
    icon: Smartphone,
    title: "کمپین پیامکی",
    desc: "ارسال انبوه، سگمنت، A/B و گزارش زنده.",
    chip: "Bulk SMS",
  },
] as const;

export default function Page() {
  return (
    <SolutionShell>
      <SiteHeader hashMode={false} />

      <section className="relative pt-32 pb-16 overflow-hidden">
        <GridBg />
        <Orbs className="inset-0" />
        <div className="container mx-auto px-6 max-w-5xl relative text-center">
          <HeroBadge>راهکارهای ترندی</HeroBadge>
          <HeroTitle pre="یک پلتفرم،" gradient="شش راهکار تخصصی" />
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mt-4">
            برای هر سناریو یک صفحهٔ اختصاصی، با طراحی، مقایسه و دموی زنده.
          </p>
        </div>
      </section>

      <Section>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-foreground/10 rounded-3xl overflow-hidden border border-border">
          {solutions.map((s, i) => (
            <motion.div
              key={s.to}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
            >
              <Link
                href={s.to}
                className="group relative block h-full bg-background p-7 hover:bg-foreground/[0.03] transition-all overflow-hidden"
              >
                <div className="absolute -top-16 -left-16 w-48 h-48 rounded-full blur-3xl opacity-0 group-hover:opacity-30 transition-opacity bg-gradient-to-br from-primary-main to-secondary-main" />
                <div className="relative">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl shadow-lg mb-5 text-foreground bg-gradient-to-br from-primary-main to-secondary-main">
                    <s.icon size={26} />
                  </div>
                  <div className="text-[10px] font-black tracking-[0.2em] text-muted-foreground/80 uppercase mb-2">
                    {s.chip}
                  </div>
                  <h3 className="text-xl font-black text-foreground mb-2">{s.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-6">{s.desc}</p>
                  <div className="inline-flex items-center gap-1.5 text-sm font-bold text-primary-main">
                    مشاهده{" "}
                    <ArrowUpLeft
                      size={14}
                      className="group-hover:-translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                    />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </Section>

      <GiantStats
        items={[
          { value: "+۵٬۲۰۰", label: "کسب‌وکار فعال" },
          { value: "+۲.۴M", label: "پیام پاسخ‌داده‌شده" },
          { value: "۹۸٪", label: "رضایت مشتری" },
          { value: "۲۴/۷", label: "پشتیبانی فارسی" },
        ]}
      />

      <Section>
        <SectionHeader eyebrow="نظر مشتریان" title="چرا ترندی را انتخاب کردند" />
        <MiniTestimonials
          items={[
            {
              name: "آرش طاهری",
              role: "Digikolah",
              quote: "یک پلتفرم برای همه چیز؛ تیم سریع‌تر شد.",
            },
            {
              name: "نگار حسینی",
              role: "Hilda Beauty",
              quote: "ترکیب اینستا + پیامک + CRM فوق‌العاده‌ست.",
            },
            {
              name: "محمد سپهر",
              role: "آکادمی موسیقی",
              quote: "Auto-Reply ثبت‌نام دوره رو خودکار کرد.",
            },
          ]}
        />
      </Section>

      <Section className="border-t border-border">
        <SectionHeader eyebrow="پرسش‌ها" title="سؤالات متداول" />
        <FAQ
          items={[
            { q: "می‌تونم فقط یکی از راهکارها رو بخرم؟", a: "بله، پلن‌ها ماژولار هستند." },
            {
              q: "همهٔ ابزارها به هم وصلند؟",
              a: "بله، یک کاربر/مشتری در همه‌ی ماژول‌ها مشترک است.",
            },
            { q: "آموزش رایگان دارید؟", a: "بله، مستندات، ویدیو و وبینار رایگان." },
          ]}
        />
      </Section>

      <FinalCTA
        title={
          <>
            راهکار <span className="brand-gradient-text">شما</span> اینجاست
          </>
        }
        desc="یک حساب، شش راهکار، بی‌نهایت رشد."
      />
      <SiteFooter />
    </SolutionShell>
  );
}
